import { useLocalization } from "@fluent/react"
import { unwrapResult } from "@reduxjs/toolkit"
import { Box, Checkbox, Fab, IconButton, Paper, TableContainer, Tooltip } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import AddIcon from "@mui/icons-material/Add"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import AccountTreeIcon from "@mui/icons-material/AccountTree"
import { useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { LoadingContainer } from "../../app/LoadingContainer"
import { QuickSearchToolbar } from "../../app/QuickSearchToolbar"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { selectSettings } from "../settings/settingsSlice"
import { StructureDefinitionDto, loadCompanyStructuresDefinitions } from "./structureSlice"
import { CloseStructureDialog } from "./dialogs/CloseStructureDialog"
import { CreateStructureDialog } from "./dialogs/CreateStructureDialog"

export const StructuresList = () => {
    const { l10n } = useLocalization()
    const { locale } = useAppSelector(selectSettings)
    const dispatch = useAppDispatch()
    const navigate = useNavigate()

    const [rows, setRows] = useState<StructureDefinitionDto[]>([])
    const [isLoading, setIsLoading] = useState(false)

    const [createDialogOpen, setCreateDialogOpen] = useState(false)
    const [editDialogOpen, setEditDialogOpen] = useState(false)
    const [closeDialogOpen, setCloseDialogOpen] = useState(false)
    const [selectedStructure, setSelectedStructure] = useState<StructureDefinitionDto | null>(null)

    const loadData = useCallback(() => {
        setIsLoading(true)
        dispatch(loadCompanyStructuresDefinitions({ onlyActive: true }))
            .then(unwrapResult)
            .then(setRows)
            .catch(error => console.error(error))
            .finally(() => setIsLoading(false))
    }, [dispatch])

    useEffect(() => {
        loadData()
    }, [loadData])

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText
        if (locale === "pl") {
            return plPL.components.MuiDataGrid.defaultProps.localeText
        }

        return defaultEnLocaleText
    }

    const columns: GridColDef[] = [
        {
            field: "code",
            headerName: l10n.getString("structures-list-code", null, "Kod"),
            width: 180,
        },
        {
            field: "name",
            headerName: l10n.getString("structures-list-name", null, "Nazwa"),
            width: 260,
            flex: 1,
        },
        {
            field: "description",
            headerName: l10n.getString("structures-list-description", null, "Opis"),
            width: 360,
        },
        {
            field: "validFrom",
            headerName: l10n.getString("structures-list-valid-from", null, "Obowiązuje od"),
            width: 170,
            valueGetter: (_, row) => row.validFrom ? new Date(row.validFrom).toISOString().split("T")[0] : "",
        },
        {
            field: "validTo",
            headerName: l10n.getString("structures-list-valid-to", null, "Obowiązuje do"),
            width: 170,
            valueGetter: (_, row) => row.validTo ? new Date(row.validTo).toISOString().split("T")[0] : "",
        },
        {
            field: "isActive",
            headerName: l10n.getString("structures-list-is-active", null, "Aktywna"),
            width: 120,
            align: "center",
            headerAlign: "center",
            sortable: false,
            filterable: false,
            valueGetter: (_, row) => row.isActive,
            renderCell: params => (
                <Checkbox
                    checked={!!params.value}
                    disabled
                    sx={{
                        color: params.value ? "success.main" : "grey.400",
                        "&.Mui-checked": { color: "success.main" },
                        "&.Mui-disabled": { color: params.value ? "success.main" : "grey.400" },
                    }}
                />
            ),
        },
        {
            field: "actions",
            headerName: l10n.getString("actions", null, "Akcje"),
            width: 170,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            align: "center",
            headerAlign: "center",
            renderCell: params => (
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.25, height: "100%" }}>
                    <Tooltip title={l10n.getString("edit", null, "Edytuj")}>
                        <IconButton
                            size="small"
                            onClick={() => {
                                setSelectedStructure(params.row)
                                setEditDialogOpen(true)
                            }}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={l10n.getString("structures-create-tree", null, "Twórz drzewko")}>
                        <IconButton
                            size="small"
                            onClick={() => navigate(`/company-structures/edit/${params.row.id}`)}
                        >
                            <AccountTreeIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>

                    <Tooltip title={l10n.getString("structures-close-action", null, "Zamknij")}>
                        <span>
                            <IconButton
                                size="small"
                                color="error"
                                disabled={!params.row.isActive}
                                onClick={() => {
                                    setSelectedStructure(params.row)
                                    setCloseDialogOpen(true)
                                }}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
            ),
        },
    ]

    if (isLoading) {
        return <LoadingContainer />
    }

    return (
        <>
            <TableContainer component={Paper}>
                <DataGrid
                    style={{ minHeight: 100, height: "calc(100vh - 200px)" }}
                    localeText={getCustomLocaleText()}
                    rows={rows}
                    columns={columns}
                    slots={{ toolbar: QuickSearchToolbar }}
                />
                <Box sx={{ display: "flex" }}>
                    <Fab
                        sx={{ margin: 1 }}
                        onClick={() => setCreateDialogOpen(true)}
                        color="secondary"
                        size="small"
                    >
                        <AddIcon />
                    </Fab>
                </Box>
            </TableContainer>

            <CreateStructureDialog
                open={createDialogOpen}
                onClose={() => setCreateDialogOpen(false)}
                onSaved={loadData}
                mode="create"
            />

            <CreateStructureDialog
                open={editDialogOpen}
                onClose={() => {
                    setEditDialogOpen(false)
                    setSelectedStructure(null)
                }}
                onSaved={loadData}
                mode="edit"
                structure={selectedStructure}
            />

            <CloseStructureDialog
                open={closeDialogOpen}
                structure={selectedStructure}
                onClose={() => {
                    setCloseDialogOpen(false)
                    setSelectedStructure(null)
                }}
                onClosed={loadData}
            />
        </>
    )
}
